/**
 * Currency entity — the three internal currencies and a user's balance.
 *
 * Mirrors the backend ``UserBalanceSerializer`` (all integer ``BigInteger`` units).
 * Values are always server-authoritative; the client only displays them.
 */

/** The three internal currency kinds (spec §4 / ТЗ). */
export type CurrencyKind = "mora" | "primogems" | "star_glitter";

/** A user's internal currency balance (integer units), as returned by the API. */
export interface UserBalance {
  readonly mora: number;
  readonly primogems: number;
  readonly star_glitter: number;
}

/** Display metadata for a currency (label + accent), used by balance widgets. */
export interface CurrencyMeta {
  readonly kind: CurrencyKind;
  readonly label: string;
  /** Short glyph/emoji for compact display. */
  readonly glyph: string;
  /** Maps to a shared-ui Badge/accent tone. */
  readonly tone: "moonstone" | "gold" | "amethyst";
}

/** Ordered display metadata for the three currencies (mora → primogems → glitter). */
export const CURRENCY_META: readonly CurrencyMeta[] = [
  { kind: "mora", label: "Мора", glyph: "🪙", tone: "moonstone" },
  { kind: "primogems", label: "Примогемы", glyph: "💎", tone: "gold" },
  { kind: "star_glitter", label: "Звёздный блеск", glyph: "✦", tone: "amethyst" },
] as const;

/** Read the unit amount for a given currency kind from a balance. */
export function balanceOf(balance: UserBalance, kind: CurrencyKind): number {
  switch (kind) {
    case "mora":
      return balance.mora;
    case "primogems":
      return balance.primogems;
    case "star_glitter":
      return balance.star_glitter;
  }
}
